import type React from "react";
import type { TranslationKey } from "@shared/types/translation-key.types";
import { Button } from "@planta-v2/pds-ui/button";
import { PorterOrb } from "@frontend/shared/presentation/components/PorterOrb";
import { i18n } from "@frontend/i18n";
import {
  DirectoryTab,
  type DirectoryTabType,
} from "../../../domain/types/tab.types";
import {
  CoffeeCupMark,
  NetworkMark,
  WavesMark,
} from "@frontend/shared/presentation/components/EmptyStateMarks";

interface DirectoryEmptyStateProps {
  readonly tab: EmptyStateKind;
  readonly searchQuery?: string;
  /** Suggestions only: the way out to the full list while Porter is still asking. */
  readonly onBrowseAll?: () => void;
}

interface TabConfigType {
  readonly titleKey: TranslationKey;
  readonly descriptionKey?: TranslationKey;
}

/** The Sent view is not a tab, but its empty state is the same plain state of the room. */
export const SENT_REQUESTS_STATE = "sentRequests" as const;
type EmptyStateKind = DirectoryTabType | typeof SENT_REQUESTS_STATE;

const TAB_CONFIG = {
  [DirectoryTab.ALL]: {
    titleKey: "user.directory.empty.all.title",
    descriptionKey: "user.directory.empty.all.description",
  },
  [DirectoryTab.REQUESTS]: {
    titleKey: "user.directory.empty.requests.title",
    descriptionKey: "user.directory.empty.requests.description",
  },
  [DirectoryTab.CONNECTED]: {
    titleKey: "user.directory.empty.connected.title",
    descriptionKey: "user.directory.empty.connected.description",
  },
  [SENT_REQUESTS_STATE]: {
    titleKey: "user.directory.empty.sentRequests.title",
    descriptionKey: "user.directory.empty.sentRequests.description",
  },
} as const satisfies Record<
  Exclude<EmptyStateKind, typeof DirectoryTab.TOP_PICKS>,
  TabConfigType
>;

const MARK_CSSCLASS = "mb-2.5 text-border";

/**
 * Porter appears only where Porter is the one doing the work — the suggestions
 * he is still assembling. Every other empty state is a plain state of the room,
 * so it gets the mark the design draws for it.
 */
function EmptyStateMark({
  tab,
}: {
  readonly tab: EmptyStateKind;
}): React.JSX.Element {
  if (tab === DirectoryTab.TOP_PICKS) {
    return <PorterOrb className="mb-2.5 h-11 w-11" />;
  }
  if (tab === DirectoryTab.REQUESTS || tab === SENT_REQUESTS_STATE) {
    return <WavesMark className={`${MARK_CSSCLASS} h-6 w-6`} />;
  }
  if (tab === DirectoryTab.CONNECTED) {
    return <NetworkMark className={`${MARK_CSSCLASS} h-6 w-6`} />;
  }
  return <CoffeeCupMark className={`${MARK_CSSCLASS} h-8 w-8`} />;
}

export function DirectoryEmptyState({
  tab,
  searchQuery,
  onBrowseAll,
}: DirectoryEmptyStateProps): React.JSX.Element {
  if (searchQuery !== undefined && searchQuery !== "") {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center py-12 px-4"
        data-testid="directory-empty-state"
      >
        <CoffeeCupMark className={`${MARK_CSSCLASS} h-8 w-8`} />
        <h2 className="mb-2.5 font-serif text-base font-normal text-foreground">
          {i18n.t("user.directory.empty.search.title")}
        </h2>
        <p className="text-pds-2xs leading-relaxed text-muted-foreground text-center">
          {i18n.t("user.directory.empty.search.description", {
            query: searchQuery,
          })}
        </p>
      </div>
    );
  }

  // Suggestions is a WORKING state, not an empty one: Porter is mid-errand.
  // Canon (WEB 29) gives it its own rhythm, a second quieter line, and a way
  // into the room while he asks — the empty tabs get none of those.
  if (tab === DirectoryTab.TOP_PICKS) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3.5 px-5 pb-12 pt-[52px]"
        data-testid="directory-empty-state"
      >
        <PorterOrb className="size-[46px]" />
        <h2 className="font-serif text-pds-lg font-normal text-foreground">
          {i18n.t("user.directory.empty.topPicks.title")}
        </h2>
        <p className="text-center text-pds-2xs leading-relaxed text-muted-foreground">
          {i18n.t("user.directory.empty.topPicks.description")}
        </p>
        <p className="text-center text-pds-micro leading-relaxed text-muted-foreground">
          {i18n.t("user.directory.empty.topPicks.note")}
        </p>
        {onBrowseAll !== undefined && (
          <Button
            variant="outline"
            onClick={onBrowseAll}
            data-testid="directory-empty-browse-all"
            className="mt-1"
          >
            {i18n.t("user.directory.empty.topPicks.browse")}
          </Button>
        )}
      </div>
    );
  }

  const config = TAB_CONFIG[tab];

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center py-12 px-4"
      data-testid="directory-empty-state"
    >
      <EmptyStateMark tab={tab} />
      <h2 className="mb-2.5 font-serif text-base font-normal text-foreground">
        {i18n.t(config.titleKey)}
      </h2>
      {"descriptionKey" in config && (
        <p className="text-pds-2xs leading-relaxed text-muted-foreground text-center">
          {i18n.t(config.descriptionKey)}
        </p>
      )}
      {onBrowseAll !== undefined && (
        <Button
          variant="outline"
          onClick={onBrowseAll}
          data-testid="directory-empty-browse-all"
          className="mt-5"
        >
          {i18n.t("user.directory.empty.browse")}
        </Button>
      )}
    </div>
  );
}
